interface FbCookies {
  fbp?: string
  fbc?: string
}

// Storage backup for a synthesized _fbc when an ad-blocker (or denied consent)
// prevents Meta Pixel from writing the cookie itself. Keyed in localStorage so
// it survives reloads within the same browser profile, which is what CAPI
// attribution windows expect.
const FBC_FALLBACK_KEY = 'hh.analytics.fbc'
// `_fbp` is normally written by Meta Pixel. We synthesize one when the Pixel
// is absent so anonymous users still contribute to Browser ID coverage.
const FBP_FALLBACK_KEY = 'hh.analytics.fbp'

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith(`${name}=`))
  return match ? match.slice(name.length + 1) : undefined
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return
  // Meta's reference Pixel uses a host-only cookie with ~90 day TTL on the
  // top-level eTLD+1. We mirror that intent with the current host; SameSite=Lax
  // keeps it sent on top-level navigations from Meta-served ad clicks.
  try {
    document.cookie = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`
  } catch {
    // Cookie write rejected (rare; e.g. policy in some embedded webviews).
  }
}

function readStorage(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // localStorage unavailable — synthesized value stays in-cookie only.
  }
}

// Meta's spec for the _fbc value when reconstructed from a click parameter:
//   fb.<subdomainIndex>.<creationUnixMs>.<fbclid>
// `subdomainIndex` is 1 for a top-level domain (e.g. example.com), 2 for one
// subdomain (shop.example.com), etc. We always store on the current document's
// host, so 1 is the correct, conservative default for our setup.
function buildFbcFromClickId(fbclid: string): string {
  return `fb.1.${Date.now()}.${fbclid}`
}

function buildFbpValue(): string {
  // Same shape as Meta Pixel's _fbp: `fb.1.<creationUnixMs>.<random64>`.
  // Random component is a 10-digit-ish number (Pixel uses Math.random()*2^31 + 1).
  const rand = Math.floor(Math.random() * 2_147_483_647) + 1
  return `fb.1.${Date.now()}.${rand}`
}

/**
 * Reads `?fbclid=` off the current URL and, if found, persists a synthesized
 * `_fbc` value (cookie + localStorage backup) when one isn't already set.
 *
 * Idempotent: subsequent calls with the same query string reuse the existing
 * stored value rather than overwriting it with a fresher timestamp, which would
 * otherwise drift the click attribution window.
 *
 * Run on bootstrap (before the first event ships) so transports always see a
 * usable `_fbc`, even when the Meta Pixel script is blocked or consent is
 * denied at landing.
 */
export function captureFbclidFromUrl(): void {
  if (typeof window === 'undefined') return

  let fbclid: string | null = null
  try {
    fbclid = new URL(window.location.href).searchParams.get('fbclid')
  } catch {
    return
  }
  if (!fbclid) return

  const existing = readCookie('_fbc') || readStorage(FBC_FALLBACK_KEY)
  if (existing && existing.endsWith(`.${fbclid}`)) return

  const value = buildFbcFromClickId(fbclid)
  // 90 days mirrors Meta's default click attribution window.
  writeCookie('_fbc', value, 60 * 60 * 24 * 90)
  writeStorage(FBC_FALLBACK_KEY, value)
}

/**
 * Ensures a stable `_fbp` exists for this browser. Meta Pixel writes one on
 * first load when consent is granted; if the Pixel never runs (blocker, denied
 * consent) we synthesize one so server-side CAPI still has a Browser ID to
 * attribute against. Same shape as the Pixel uses, so collisions with a real
 * Pixel-set value cannot occur (different timestamp + random).
 *
 * Idempotent — only writes when both cookie and localStorage backup are empty.
 */
export function ensureFbp(): void {
  if (typeof window === 'undefined') return
  const existing = readCookie('_fbp') || readStorage(FBP_FALLBACK_KEY)
  if (existing) return

  const value = buildFbpValue()
  writeCookie('_fbp', value, 60 * 60 * 24 * 90)
  writeStorage(FBP_FALLBACK_KEY, value)
}

export function readFbCookies(): FbCookies {
  return {
    fbp: readCookie('_fbp') || readStorage(FBP_FALLBACK_KEY),
    fbc: readCookie('_fbc') || readStorage(FBC_FALLBACK_KEY),
  }
}

/**
 * Reads the GA4 client_id from the _ga cookie.
 * Returns undefined when the cookie is absent (denied consent, new visitor, or blocker).
 * Consent gating for personalised-ads signals is the backend's responsibility.
 */
export function readGaClientId(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : undefined
}
